import { useState, useEffect } from "react";
import { useRoute } from "wouter";
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import ManagerLayout from "@/components/layout/ManagerLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Sparkles, BadgeDollarSign, ArrowLeft, Calendar, BrainCircuit, RefreshCw } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { addMonths, format } from "date-fns";
import { apiRequest } from "@/lib/api";

interface EditPropertyPageProps {
  id?: number;
  isAdmin?: boolean;
}

export default function EditPropertyPage({ id, isAdmin = false }: EditPropertyPageProps) {
  // If id is provided via props, use it, otherwise try to get it from the route
  const [, params] = useRoute(isAdmin ? "/admin/properties/:id/edit" : "/manager/edit-property/:id");
  const propertyId = id || parseInt(params?.id || "0");
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("");
  const [address, setAddress] = useState("");
  const [bedrooms, setBedrooms] = useState<number>(1);
  const [bathrooms, setBathrooms] = useState<number>(1);
  const [maxGuests, setMaxGuests] = useState<number>(2);
  const [basePrice, setBasePrice] = useState<number>(0);
  const [features, setFeatures] = useState<string[]>([]);
  const [featureInput, setFeatureInput] = useState("");
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [showSmartPricing, setShowSmartPricing] = useState(false);
  const [propertyType, setPropertyType] = useState("Apartment");
  
  // Common amenities list
  const commonAmenities = [
    "Wi-Fi", "Air Conditioning", "Kitchen", "Washing Machine", 
    "Parking", "Pool", "TV", "Gym", "Balcony", "Workspace"
  ];
  
  // Property type options
  const propertyTypes = [
    "Apartment", "Villa", "Townhouse", "Penthouse", "Studio"
  ];
  
  // Fetch property data
  const { data: property, isLoading, error } = useQuery({
    queryKey: ['properties', propertyId],
    queryFn: async () => {
      const res = await apiRequest('GET', `properties/${propertyId}`);
      return res;
    },
    enabled: propertyId > 0
  });
  
  // Populate form when property data is loaded
  useEffect(() => {
    if (property) {
      setTitle(property.title || "");
      setDescription(property.description || "");
      setArea(property.area || "");
      setAddress(property.address || "");
      setBedrooms(property.bedrooms ?? 1);
      setBathrooms(property.bathrooms ?? 1);
      setMaxGuests(Number(property.maxGuests) || 2);
      setBasePrice(property.basePrice || 0);
      setFeatures(property.features || []);
      setExistingImages(property.images || []);
      setShowSmartPricing(property.useSmartPricing || false);
    }
  }, [property]);
  
  const handleAddFeature = () => {
    if (featureInput && !features.includes(featureInput)) {
      setFeatures([...features, featureInput]);
      setFeatureInput("");
    }
  };
  
  const handleRemoveFeature = (feature: string) => {
    setFeatures(features.filter(f => f !== feature));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("area", area);
      formData.append("address", address);
      formData.append("bedrooms", bedrooms.toString());
      formData.append("bathrooms", bathrooms.toString());
      formData.append("maxGuests", maxGuests.toString());
      formData.append("basePrice", basePrice.toString());
      formData.append("features", JSON.stringify(features));
      formData.append("useSmartPricing", (showSmartPricing).toString());
      
      // Add any new images
      uploadedImages.forEach(image => {
        formData.append("images", image);
      });
      
      // Use the updated apiRequest signature: method, url, data (FormData), customConfig
      const response = await apiRequest('PUT', `properties/${propertyId}`, formData, {
        credentials: "include",
      });
      
      // if (!response.ok) {
      //   throw new Error("Failed to update property");
      // }
      
      toast({
        title: "Success",
        description: "Property updated successfully",
        variant: "success",
      });
      
      // Redirect based on user role
      window.location.href = isAdmin ? "/admin/properties" : "/manager/properties";
    } catch (error) {
      console.error("Error updating property:", error);
      toast({
        title: "Error",
        description: "Failed to update property",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setUploadedImages([...uploadedImages, ...fileArray]);
    }
  };
  
  const handleRemoveUploadedImage = (index: number) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };
  
  // Helper function to render content with the appropriate layout
  const renderWithLayout = (content: React.ReactNode) => {
    if (isAdmin) {
      return <AdminLayout>{content}</AdminLayout>;
    } else {
      return <ManagerLayout>{content}</ManagerLayout>;
    }
  };

  // Return URL after saving
  const getReturnUrl = () => {
    if (isAdmin) {
      return "/admin/properties";
    } else {
      return "/manager/properties";
    }
  };

  if (isLoading) {
    return renderWithLayout(
      <div className="container py-10 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return renderWithLayout(
      <div className="container py-10">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-4">Error Loading Property</h2>
              <p className="text-muted-foreground mb-4">
                We couldn't load the property information. Please try again.
              </p>
              <Button onClick={() => window.location.href = getReturnUrl()}>
                Return to Properties
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return renderWithLayout(
    <>
      <Helmet>
        <title>Edit Property | StayMiddle</title>
      </Helmet>
      
      <div className="container py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Edit Property</h1>
          <div className="flex gap-2">
            {propertyId && (
              <Button
                asChild
                variant="outline"
                className="mr-2"
              >
                <a
                  href={`/properties/${propertyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Property
                </a>
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => window.location.href = getReturnUrl()}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Properties
            </Button>
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Property Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="title">Property Code *</Label>
                  <Input
                    id="title"
                    value={title}
                    readOnly
                    className="bg-muted cursor-not-allowed"
                    placeholder="DUB-DT-0001"
                    required
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Property code cannot be changed once created.
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="area">Area/Neighborhood *</Label>
                  <Input
                    id="area"
                    value={area}
                    onChange={e => setArea(e.target.value)}
                    placeholder="Downtown Dubai, Dubai Marina, etc."
                    required
                    readOnly={Boolean(title && title.match(/^DUB-[A-Z]{2}-\d{4}$/))}
                    className={Boolean(title && title.match(/^DUB-[A-Z]{2}-\d{4}$/)) ? "bg-muted cursor-not-allowed" : ""}
                  />
                  {Boolean(title && title.match(/^DUB-[A-Z]{2}-\d{4}$/)) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Area cannot be changed once property code is generated.
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Building name, street, etc."
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="propertyType">Property Type *</Label>
                  <Select
                    value={propertyType}
                    onValueChange={value => setPropertyType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="bedrooms">Bedrooms *</Label>
                  <Select
                    value={bedrooms.toString()}
                    onValueChange={value => setBedrooms(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bedrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6].map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 0 ? 'Studio' : num === 1 ? 'Bedroom' : 'Bedrooms'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="bathrooms">Bathrooms *</Label>
                  <Select
                    value={bathrooms.toString()}
                    onValueChange={value => setBathrooms(parseFloat(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bathrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Bathroom</SelectItem>
                      <SelectItem value="1.5">1.5 Bathrooms</SelectItem>
                      <SelectItem value="2">2 Bathrooms</SelectItem>
                      <SelectItem value="2.5">2.5 Bathrooms</SelectItem>
                      <SelectItem value="3">3 Bathrooms</SelectItem>
                      <SelectItem value="3.5">3.5 Bathrooms</SelectItem>
                      <SelectItem value="4">4 Bathrooms</SelectItem>
                      <SelectItem value="4.5">4.5 Bathrooms</SelectItem>
                      <SelectItem value="5">5 Bathrooms</SelectItem>
                      <SelectItem value="5.5">5.5 Bathrooms</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="maxGuests">Maximum Guests *</Label>
                  <Select
                    value={maxGuests.toString()}
                    onValueChange={value => setMaxGuests(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select max guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'Guest' : 'Guests'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div> 
              {propertyId && (
              <Button
                variant="default"
                onClick={() => window.location.href = `/manager/calendar/${propertyId}`}
              >
                View Calendar
              </Button>
            )}
            {property && property.airbnbListingId && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    setIsSubmitting(true);
                    const response = await apiRequest('POST', `properties/${propertyId}/sync-airbnb-calendar`, {
                      airbnbListingId: property.airbnbListingId
                    });
                    if (response.success) {
                    toast({
                      title: 'Calendar Synced',
                      description: response.message || 'Airbnb calendar has been synced and updated.',
                    });
                    window.location.reload();
                  }
                  } catch (error) {
                    toast({
                      title: 'Sync Failed',
                      description: (error as any).message || 'Failed to sync with Airbnb calendar',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting}
                className="ml-2"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Calendar with Airbnb
              </Button>
            )}
              </div>
              </div>

              
              <div className="col-span-1 md:col-span-2">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Manual Pricing Card */}
                  <div
                    className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${!showSmartPricing ? 'border-primary ring-2 ring-primary' : 'border-muted'}`}
                    onClick={() => {
                      setShowSmartPricing(false);
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <div className="flex items-center mb-2">
                      <BadgeDollarSign className="text-muted-foreground mr-2 h-5 w-5" />
                      <span className="font-semibold text-lg">Manual Pricing</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">Set your own base price for this property.</p>
                    {showSmartPricing === false && (
                      <div className="flex items-center mt-2 gap-2">
                        <Input
                          id="basePrice"
                          type="number"
                          min={0}
                          value={basePrice}
                          onChange={e => setBasePrice(parseInt(e.target.value))}
                          placeholder="500"
                          required
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="ml-2"
                          onClick={async () => {
                            try {
                              const response = await apiRequest('POST', 'smart-pricing', {
                                area,
                                bedrooms,
                                bathrooms,
                                propertyType,
                                amenities: features
                              });
                              const data =  response;
                              setBasePrice(data.suggestedPrice);
                              toast({
                                title: "Smart Price Generated",
                                description: `Suggested price: ${data.suggestedPrice} AED based on location and property details.`,
                              });
                            } catch (error) {
                              toast({
                                title: "Price Generation Failed",
                                description: (error as any).message || "Failed to generate price suggestion",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          Suggest Price
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Smart AI Pricing Card */}
                  <div
                    className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${showSmartPricing ? 'border-primary ring-2 ring-primary' : 'border-muted'}`}
                    onClick={() => {
                      setShowSmartPricing(true);
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <div className="flex items-center mb-2">
                      <BrainCircuit className="mr-2 h-5 w-5 text-primary" />
                      <span className="font-semibold text-lg">Smart AI Pricing</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">Let AI optimize your property pricing based on market trends, location, and seasonal demand.</p>
                    {showSmartPricing === true && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="ml-2 h-7 mb-2"
                          onClick={() => {
                            setShowSmartPricing(true);
                          }}
                          disabled={showSmartPricing}
                        >
                          {showSmartPricing ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4 mr-1" />
                          )}
                          Generate Smart Price
                        </Button>
                        {showSmartPricing && (
                          <p className="text-sm text-muted-foreground mt-1">Generating optimal price based on market data...</p>
                        )}
                        <div className="mt-2 text-xs text-muted-foreground">
                          Last price update: {property?.lastAiPriceUpdate ?
                            new Date(property.lastAiPriceUpdate).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Not updated yet'}
                        </div>
                        <ul className="list-disc list-inside space-y-1 pl-2 mt-2">
                          <li>Location attractiveness in Dubai</li>
                          <li>Seasonal demand patterns</li>
                          <li>Day-of-week pricing (weekends vs weekdays)</li>
                          <li>Similar property comparisons</li>
                          <li>Market trends for your property type</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="description" className="flex justify-between items-center">
                  <span>Description *</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={async () => {
                      if (!description.trim()) {
                        toast({
                          title: "Description Required",
                          description: "Please enter a basic description first before enhancing.",
                          variant: "destructive"
                        });
                        return;
                      }
                      
                      try {
                        setIsSubmitting(true);
                        
                        const data = {
                          description,
                          title,
                          area,
                          propertyType,
                          bedrooms,
                          bathrooms,
                          features
                        };
                        const response = await apiRequest('POST', '/enhance-description', data);
                        
                        if (!response.ok) {
                          throw new Error('Failed to enhance description');
                        }
                        
                        const enhancedData = await response.json();
                        setDescription(enhancedData.enhancedDescription);
                        
                        toast({
                          title: "Description Enhanced",
                          description: "Your property description has been improved using AI."
                        });
                      } catch (error) {
                        console.error('Error enhancing description:', error);
                        toast({
                          title: "Enhancement Failed",
                          description: "Unable to enhance the description. Please try again later.",
                          variant: "destructive"
                        });
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}
                    disabled={isSubmitting || !description.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-1" />
                        Enhance with AI
                      </>
                    )}
                  </Button>
                </Label>
                {/* Enhanced description preview panel with HTML rendering */}
                {description.includes('<b>') || description.includes('<br') ? (
                  <div className="mb-3 border rounded-md p-3 bg-muted/30">
                    <div className="text-sm font-semibold mb-2">Enhanced Description Preview:</div>
                    <div 
                      className="prose max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  </div>
                ) : null}
                
                <Textarea
                  id="description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe the property..."
                  className="min-h-[150px]"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Tip: Enter a basic description and click "Enhance with AI" to create a professional, detailed description.
                  {description.includes('<b>') || description.includes('<br') ? 
                    " HTML formatting is shown in the preview above." : ""}
                </p>
              </div>
              
              <div>
                <Label>Features/Amenities</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {features.map(feature => (
                    <div 
                      key={feature}
                      className="flex items-center bg-muted px-3 py-1 rounded-full"
                    >
                      <span className="mr-2">{feature}</span>
                      <button 
                        type="button"
                        onClick={() => handleRemoveFeature(feature)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center gap-2">
                  <Input
                    value={featureInput}
                    onChange={e => setFeatureInput(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleAddFeature}
                  >
                    Add
                  </Button>
                </div>
                
                <div className="mt-3">
                  <Label>Quick Add:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {commonAmenities.map(amenity => (
                      <Button
                        key={amenity}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!features.includes(amenity)) {
                            setFeatures([...features, amenity]);
                          }
                        }}
                        disabled={features.includes(amenity)}
                        className={features.includes(amenity) ? "opacity-50" : ""}
                      >
                        {amenity}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Property Images</Label>
                
                {existingImages.length > 0 && (
                  <div className="mb-4">
                    <Label className="text-sm text-muted-foreground mb-2 block">Existing Images:</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {existingImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-md overflow-hidden border">
                            <img 
                              src={image.imageUrl} 
                              alt={`Property ${index}`} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {image.isPrimary && (
                            <div className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded-full">
                              Primary
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                            {!image.isPrimary && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await apiRequest('PATCH', `properties/${propertyId}/images/${image.id}/primary`, {
                                      credentials: 'include',
                                    });
                                    
                                    // Update the UI by refreshing the images
                                    const updatedImages = existingImages.map(img => ({
                                      ...img,
                                      isPrimary: img.id === image.id
                                    }));
                                    setExistingImages(updatedImages);
                                    
                                    toast({
                                      variant: "success",
                                      title: "Primary image updated",
                                      description: "This image is now set as the primary image for this property."
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Failed to update primary image",
                                      description: "Please try again later.",
                                      variant: "destructive"
                                    });
                                  }
                                }}
                                className="text-white bg-primary/80 hover:bg-primary text-xs p-1 rounded"
                              >
                                Set Primary
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                if (confirm("Are you sure you want to delete this image? This cannot be undone.")) {
                                  try {
                                    await apiRequest('DELETE', `properties/${propertyId}/images/${image.id}`, {
                                      credentials: 'include',
                                    });
                                    
                                    // Update the UI by removing the deleted image
                                    setExistingImages(existingImages.filter(img => img.id !== image.id));
                                    
                                    toast({
                                      title: "Image deleted",
                                      description: "The image has been removed from this property."
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Failed to delete image",
                                      description: "Please try again later.",
                                      variant: "destructive"
                                    });
                                  }
                                }
                              }}
                              className="text-white bg-destructive/80 hover:bg-destructive text-xs p-1 rounded ml-1"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {uploadedImages.length > 0 && (
                  <div className="mb-4">
                    <Label className="text-sm text-muted-foreground mb-2 block">New Uploads:</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-md overflow-hidden border">
                            <img 
                              src={URL.createObjectURL(image)} 
                              alt={`New upload ${index}`} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveUploadedImage(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-4">
                  <Label htmlFor="images">Add New Photos</Label>
                  <Input
                    id="images"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    You can select multiple images at once. Maximum 5MB per image.
                  </p>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      {/* Sticky Footer for Update Button */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t z-50 py-3 px-4 flex justify-end shadow-md">
        <Button 
          type="submit" 
          className="px-8"
          disabled={isSubmitting}
          form={undefined} // We'll trigger the form manually
          onClick={() => {
            // Find the form and trigger submit
            const form = document.querySelector('form');
            if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            "Update Property"
          )}
        </Button>
      </div>
    </>
  );
}